import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './LSTMPredictor.css';

function LSTMPredictor() {
  const navigate = useNavigate();
  const [inputText, setInputText] = useState('');
  const [predictions, setPredictions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [modelInfo, setModelInfo] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const API_BASE = 'https://imdb-sentiment-backend.onrender.com/api/lstm';

  useEffect(() => {
    fetchModelConfig();
  }, []);

  const fetchModelConfig = async () => {
    try {
      const response = await fetch(`${API_BASE}/config`);
      const data = await response.json();
      setModelInfo(data);
    } catch (err) {
      console.error('Failed to fetch model config:', err);
    }
  };

  const handlePredict = async () => {
    if (!inputText.trim()) {
      setError('Please enter some text');
      return;
    }

    setLoading(true);
    setError('');
    setPredictions([]);

    try {
      const response = await fetch(`${API_BASE}/predict`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: inputText,
          num_predictions: 5
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setPredictions(data.predictions);
      } else {
        setError(data.error || 'Prediction failed');
      }
    } catch (err) {
      setError('Failed to connect to server. The model may be loading...');
    } finally {
      setLoading(false);
    }
  };

  const handlePredictionClick = (word) => {
    setInputText(prev => prev + ' ' + word);
    setPredictions([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handlePredict();
    }
  };

  const samplePrompts = [
    "I think this is",
    "The best way to",
    "I want to",
    "What do you think about",
    "This is really"
  ];

  const loadSamplePrompt = (prompt) => {
    setInputText(prompt);
    setPredictions([]);
    setError('');
  };

  return (
    <>
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            <h2>How This AI Works</h2>

            <div className="explanation-section">
              <h3>Technical Explanation</h3>
              <p>
                <strong>Step 1:</strong> Download and load Reddit comment dataset containing millions of conversational text samples.
                <strong> Step 2:</strong> Clean and tokenize the text, converting words into numerical tokens.
                <strong> Step 3:</strong> Load pre-trained GloVe word embeddings (300-dimensional vectors) that capture semantic relationships between words.
                <strong> Step 4:</strong> Create sequences of words as training data, where each sequence predicts the next word.
                <strong> Step 5:</strong> Build an LSTM (Long Short-Term Memory) recurrent neural network with an embedding layer (using GloVe), LSTM layers (maintaining context through memory cells), and a dense output layer with softmax activation for word prediction.
                <strong> Step 6:</strong> Train the model to minimize categorical cross-entropy loss, teaching it to predict the most likely next word given the input sequence.
                <strong> Step 7:</strong> Deploy via Flask API where input text is tokenized, passed through the LSTM, and returns the top predicted words with probability scores.
              </p>
            </div>

            <div className="explanation-section simply-put">
              <h3>Simply Put</h3>
              <p>
                The AI learned how people naturally write by reading millions of Reddit comments. It noticed patterns like 'I want to' usually followed by action words like 'go,' 'eat,' or 'learn.' It remembers the context of what you've already typed (not just the last word) to make smarter predictions. It's like having an assistant who's read so many conversations that it can guess what you're about to say next.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="lstm-page">
        <div className="lstm-container">
          <button onClick={() => navigate('/')} className="back-button">
            ← Back to Portfolio
          </button>
          <button className="info-btn" onClick={() => setShowModal(true)}>
            ℹ️ How It Works
          </button>

          <header className="lstm-header">
            <h1>LSTM Next Word Predictor</h1>
            <p className="subtitle">AI-powered text completion using recurrent neural networks</p>
          </header>

        <main className="lstm-main">
          <div className="input-section">
            <label htmlFor="textInput">Enter your text:</label>
            <textarea
              id="textInput"
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a sentence here... (e.g., 'I want to')"
              rows={3}
            />

            <div className="sample-prompts">
              <p>Try these samples:</p>
              <div className="sample-buttons">
                {samplePrompts.map((prompt, idx) => (
                  <button
                    key={idx}
                    onClick={() => loadSamplePrompt(prompt)}
                    className="sample-button"
                  >
                    {prompt}
                  </button>
                ))}
              </div>
            </div>

            <div className="button-group">
              <button
                onClick={handlePredict}
                disabled={loading}
                className="btn btn-primary"
              >
                {loading ? 'Predicting...' : 'Predict Next Word'}
              </button>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {predictions.length > 0 && (
            <div className="results-section">
              <h2>Top Predictions</h2>
              <p className="hint">Click a word to add it to your text</p>
              <div className="predictions-grid">
                {predictions.map((pred, index) => (
                  <div
                    key={index}
                    className="prediction-card"
                    onClick={() => handlePredictionClick(pred.word)}
                  >
                    <span className="prediction-word">{pred.word}</span>
                    <span className="prediction-prob">
                      {(pred.probability * 100).toFixed(1)}%
                    </span>
                    <div
                      className="probability-bar"
                      style={{ width: `${pred.probability * 100}%` }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {modelInfo && (
            <div className="model-info">
              <h3>Model Architecture</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Sequence Length</span>
                  <span className="info-value">{modelInfo.sequence_length} words</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Vocabulary Size</span>
                  <span className="info-value">{modelInfo.vocab_size?.toLocaleString()} words</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Embedding Dimension</span>
                  <span className="info-value">{modelInfo.embedding_dim}D GloVe</span>
                </div>
              </div>
            </div>
          )}

          <div className="about-section">
            <h3>About This Model</h3>
            <p>
              This LSTM (Long Short-Term Memory) neural network was trained on Reddit comments
              to predict the next word in a sentence. It uses pretrained GloVe embeddings to
              understand word relationships and context.
            </p>
            <p>
              <strong>Tech Stack:</strong> TensorFlow/Keras, Python, Flask, React
            </p>
          </div>
        </main>
      </div>
    </div>
    </>
  );
}

export default LSTMPredictor;
